/**
 * Archetype balance workbench data.
 *
 * Every number here is computed by the backend's archetype services — this
 * layer fetches, caches and shapes for display, and deliberately calculates
 * nothing. The previous version recomputed a "total power" of its own by
 * summing stat values, which meant the page could disagree with the game about
 * what a build was worth and nobody could tell which was right.
 */

import { GameMasterArchetypesGameApi } from '@/api/backendService.js';

const GRADES = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0];

class ArchetypeService {
  constructor() {
    this.api = GameMasterArchetypesGameApi;
    this.rosterCache = null;
    this.profileCache = new Map();
    this.tableCache = new Map();
    this.modelCache = null;
  }

  get grades() {
    return GRADES;
  }

  /** Every archetype, with its derived metatype and any data warnings. */
  async loadRoster({ force = false } = {}) {
    if (this.rosterCache && !force) {
      return this.rosterCache;
    }
    const response = await this.api.gamemasterArchetypesList();
    this.rosterCache = response.data;
    return this.rosterCache;
  }

  /** The whole roster measured at one grade, with the band medians. */
  async loadBalanceTable(grade) {
    if (this.tableCache.has(grade)) {
      return this.tableCache.get(grade);
    }
    const response = await this.api.gamemasterArchetypesBalanceRetrieve(grade);
    this.tableCache.set(grade, response.data);
    return response.data;
  }

  /** One archetype, grade by grade. */
  async loadProfile(archetypeId) {
    if (this.profileCache.has(archetypeId)) {
      return this.profileCache.get(archetypeId);
    }
    const response = await this.api.gamemasterArchetypesRetrieve(archetypeId);
    this.profileCache.set(archetypeId, response.data);
    return response.data;
  }

  /** The rating model's coefficients, so the page can explain its arithmetic. */
  async loadModel() {
    if (this.modelCache) {
      return this.modelCache;
    }
    const response = await this.api.gamemasterArchetypesModelRetrieve();
    this.modelCache = response.data;
    return this.modelCache;
  }

  clearCache() {
    this.rosterCache = null;
    this.profileCache.clear();
    this.tableCache.clear();
    this.modelCache = null;
  }

  /**
   * The grade slice of a loaded profile.
   *
   * Grades descend into mastery, so this is a lookup rather than an index.
   */
  gradeSlice(profile, grade) {
    return profile?.grades?.find((entry) => entry.grade === grade) ?? null;
  }

  /**
   * Skills grouped by the school that grants them, strongest group first.
   *
   * Grouping by school rather than listing flat is what makes a build legible:
   * it shows what each rung of the progression actually bought.
   */
  skillsBySchool(gradeProfile) {
    const groups = new Map();
    (gradeProfile?.skills ?? []).forEach((skill) => {
      const key = skill.school ?? 'Unaffiliated';
      if (!groups.has(key)) {
        groups.set(key, { school: key, skills: [], bestPerAp: 0 });
      }
      const group = groups.get(key);
      group.skills.push(skill);
      group.bestPerAp = Math.max(group.bestPerAp, skill.power_per_ap);
    });
    return [...groups.values()].sort((a, b) => b.bestPerAp - a.bestPerAp);
  }

  /** How a value compares with the roster median, for band colouring. */
  bandPosition(value, median, tolerance = 0.5) {
    if (!median || !value) {
      return 'even';
    }
    if (value > median * (1 + tolerance)) return 'high';
    if (value < median * (1 - tolerance)) return 'low';
    return 'even';
  }
}

export const archetypeService = new ArchetypeService();
export default archetypeService;
